import { useState, useContext, useEffect } from 'react';
import { XMarkIcon } from '@heroicons/react/24/outline';
import { useNavigate, useLocation } from 'react-router-dom';
import CartContext from '../../Context/Cart/CartContext';
import PaymentDrawer from '../../components/Payments';
import OrderDetails from '../../components/OrderDetails';
import ItemCustomization from '../../components/productDetails/ItemCustomization';
import { useBrand } from '../../Context/Brand/BrandContext';
import TableSessionContext from '../../Context/TableSession/TableSessionContext';
import { toast } from 'react-hot-toast';

const CartPage = ({ inline = false, onCloseInline = null, onSuccessInline = null, tableNumber: propTableNumber, merchantId: propMerchantId, merchantName: propMerchantName }) => {
  const { brand } = useBrand();
  const accentColor = brand.primaryColor || '#1A7F6D';
  const navigate = useNavigate();
  const location = useLocation();
  const { cartItems, removeFromCart, addToCart, clearCart } = useContext(CartContext);
  const { session: storedTableSession, participant, syncDraft, submit, restore: restoreTableSession } = useContext(TableSessionContext);
  const [marketingOptIn, setMarketingOptIn] = useState(false);
  const [showPaymentDrawer, setShowPaymentDrawer] = useState(false);
  const [sendingSharedItems, setSendingSharedItems] = useState(false);
  const [visible, setVisible] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  
  const tableNumber = propTableNumber || location.state?.table_number || '';
  const merchantId = propMerchantId || location.state?.merchantId;
  const merchantName = propMerchantName || location.state?.merchantName;
  const requestedSessionId = location.state?.table_session_id || '';
  const tableSession = storedTableSession
    && storedTableSession.restaurant_id === merchantId
    && String(storedTableSession.restaurant_tables?.table_number || '') === String(tableNumber)
    ? storedTableSession
    : null;
  const [orderType, setOrderType] = useState(location.state?.orderType || (tableNumber && tableNumber !== '0' ? 'dine_in' : 'takeaway'));
  const isTakeaway = orderType === 'takeaway';
  const isSharedDineIn = !isTakeaway && Boolean(tableSession?.id);
  const sharedTableIsSettling = isSharedDineIn && tableSession?.status === 'settling';

  const continueOrder = async () => {
    if (!isSharedDineIn) {
      setShowPaymentDrawer(true);
      return;
    }
    if (sharedTableIsSettling || sendingSharedItems) return;

    setSendingSharedItems(true);
    try {
      await syncDraft(cartItems);
      const result = await submit({ submitAll: false, marketingOptIn });
      const roundNumber = result?.submission?.round_number;
      clearCart();
      toast.success(roundNumber ? `Round ${roundNumber} sent to the kitchen` : 'Your items were sent to the kitchen');
      handleClose();
    } catch (error) {
      toast.error(error?.message || 'Your items could not be sent. Please try again.');
    } finally {
      setSendingSharedItems(false);
    }
  };

  useEffect(() => {
    setVisible(true);
  }, []);

  useEffect(() => {
    if (inline || !merchantId || !tableNumber || tableNumber === '0' || tableNumber === 'Online') return;
    restoreTableSession({ restaurantId: merchantId, tableRef: tableNumber, sessionId: requestedSessionId });
  }, [inline, merchantId, requestedSessionId, restoreTableSession, tableNumber]);

  const handleClose = () => {
    setVisible(false);
    setTimeout(() => {
      if (inline && onCloseInline) {
        onCloseInline();
      } else {
        navigate(-1);
      }
    }, 200);
  };

  const handleEditItem = (product) => {
    const selections = {};
    (product.selectedOptions || []).forEach(opt => {
      if (opt.optionId) selections[opt.groupId] = opt.optionId;
    });
    setEditingItem({
      item: product,
      initialSelections: selections,
      initialInstructions: product.specialInstructions || '',
      initialQuantity: product.quantity,
      oldItemId: product.item_public_id,
    });
  };

  const handleSaveEdit = (newCartItem) => {
    removeFromCart({ item_public_id: editingItem.oldItemId });
    addToCart(newCartItem);
    setEditingItem(null);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-end">
      <div
        className={`absolute inset-0 bg-black/40 transition-opacity duration-200 ${visible ? 'opacity-100' : 'opacity-0'}`}
        onClick={handleClose}
      />
      <div
        className={`relative w-full max-h-[90vh] bg-white rounded-t-2xl flex flex-col transition-transform duration-200 ease-out ${visible ? 'translate-y-0' : 'translate-y-full'}`}
      >
        <div className="flex items-center justify-between px-5 pt-4 pb-2 border-b border-gray-100">
          <div className="flex items-center gap-3">
            <div className="w-2 h-2 rounded-full" style={{ backgroundColor: accentColor }} />
            <h1 className="text-lg font-semibold text-gray-900">Order Summary</h1>
            <span className="text-xs text-gray-400 bg-gray-50 rounded-full px-2 py-0.5">
              {isTakeaway ? 'Takeaway' : (tableNumber ? `Table ${tableNumber}` : 'Dine in')}
            </span>
          </div>
          {!showPaymentDrawer && (
            <button onClick={handleClose} className="p-1.5 hover:bg-gray-100 rounded-full transition-colors">
              <XMarkIcon className="w-5 h-5 text-gray-500" />
            </button>
          )}
        </div>

        <div className="flex-1 overflow-y-auto px-5 py-4">
          {cartItems.length === 0 ? (
            <div className="text-center py-16">
              <div className="w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-4" style={{ backgroundColor: `${accentColor}15` }}>
                <svg className="w-10 h-10" style={{ color: accentColor }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M16 11V7a4 4 0 00-8 0v4M5 9h14l1 12H4L5 9z" />
                </svg>
              </div>
              <h3 className="text-base font-medium text-gray-900 mb-1">Your cart is empty</h3>
              <p className="text-sm text-gray-400 mb-6">Add items to get started</p>
              <button
                onClick={handleClose}
                className="inline-flex items-center px-5 py-2.5 text-sm font-medium rounded-full text-white transition-colors"
                style={{ backgroundColor: accentColor }}
              >
                Browse Menu
              </button>
            </div>
          ) : (
            <>
              <div className="mb-4 grid grid-cols-2 rounded-2xl border border-gray-200 bg-white p-1">
                {[
                  { id: 'dine_in', label: tableNumber && tableNumber !== '0' ? `Dine in - Table ${tableNumber}` : 'Dine in' },
                  { id: 'takeaway', label: 'Takeaway' },
                ].map((option) => (
                  <button
                    key={option.id}
                    type="button"
                    onClick={() => setOrderType(option.id)}
                    className="h-9 rounded-xl text-[13px] font-semibold transition"
                    style={orderType === option.id ? { backgroundColor: `${accentColor}12`, color: '#111827' } : { color: '#737373' }}
                  >
                    {option.label}
                  </button>
                ))}
              </div>
              {isSharedDineIn && participant?.customer_id && (
                <label className="mb-4 flex cursor-pointer items-start gap-3 py-2">
                  <input type="checkbox" checked={marketingOptIn} onChange={(event) => setMarketingOptIn(event.target.checked)}
                    disabled={sendingSharedItems} className="mt-1 h-5 w-5 shrink-0 rounded border-gray-300" style={{ accentColor }} />
                  <span className="min-w-0">
                    <span className="block text-sm font-medium leading-6 text-gray-900">Send me offers and updates from {merchantName || brand.restaurantName || 'this restaurant'}.</span>
                    <span className="mt-1 block text-xs leading-5 text-gray-500">Optional. This is just for you, not everyone at the table. Change your mind anytime in your account or by contacting the restaurant.</span>
                  </span>
                </label>
              )}
              <OrderDetails
                cartItems={cartItems}
                toggleShowPayment={continueOrder}
                showPayment={showPaymentDrawer}
                onEditItem={handleEditItem}
                actionLabel={isSharedDineIn ? (sharedTableIsSettling ? 'Waiting for payment' : 'Send my items') : undefined}
                actionPending={sendingSharedItems}
                actionDisabled={sharedTableIsSettling}
                actionHint={isSharedDineIn
                  ? (sharedTableIsSettling
                    ? 'This table is settling the last round. New items can be sent after payment is recorded.'
                    : 'Only your items will be sent. Everyone else can keep choosing.')
                  : ''}
              />
            </>
          )}
        </div>
      </div>

      {editingItem && (
        <div className="fixed inset-0 z-[60] flex items-end justify-center bg-black/40 px-0 pt-8 sm:items-center sm:px-4">
          <div className="h-[90vh] w-full overflow-hidden rounded-t-[26px] bg-white shadow-2xl sm:max-w-md sm:rounded-[26px]">
            <ItemCustomization
              item={editingItem.item}
              initialSelections={editingItem.initialSelections}
              initialInstructions={editingItem.initialInstructions}
              initialQuantity={editingItem.initialQuantity}
              onClose={() => setEditingItem(null)}
              onSave={handleSaveEdit}
            />
          </div>
        </div>
      )}

      {showPaymentDrawer && (
        <PaymentDrawer 
          onClose={() => setShowPaymentDrawer(false)}
          table={tableNumber}
          merchantId={merchantId}
          merchantName={merchantName}
          orderType={orderType}
          onSuccess={(orderId) => {
            if (inline && onSuccessInline) {
              onSuccessInline(orderId);
            } else {
              navigate('/my-account/orders', { state: { orderId } });
            }
          }}
        />
      )}
    </div>
  );
};

export default CartPage;
